import { z } from 'zod';
import { offerRequestAttachmentSchema } from '../../offer-request-management/attachment.js';
import { offerRequestBuildingTypeSchema } from '../../offer-request-management/buildingType.js';
import { offerRequestInfoFieldSchema } from '../../offer-request-management/infoField.js';
import { localizedTextSchema } from '../../offer-request-management/localizedText.js';
import { offerRequestProjectSizeSchema } from '../../offer-request-management/projectSize.js';
import { offerRequestReportStatusSchema, settableOfferRequestReportStatusSchema, } from '../../offer-request-management/reportStatus.js';
import { offerRequestTimeConstraintSchema } from '../../offer-request-management/timeConstraint.js';
import { partnerCockpitReimbursementStateSchema } from '../reimbursement/reimbursement.js';
export const partnerCockpitOfferRequestLanguageSchema = z.enum(['de-CH', 'en-CH', 'fr-CH', 'it-CH']);
// A lead reaches the partner either through the waterfall partner matching, or, for offer requests forwarded before
// that existed, as a bare assignment with no matching.
export const partnerCockpitOfferRequestSourceSchema = z.enum(['matching', 'assignment']);
// A Devis assignment is scaled to at most 3 credits and then doubled, so 6 is the ceiling — not the 5
// the estimated-volume mapping tops out at.
export const PARTNER_COCKPIT_OFFER_REQUEST_MAX_CREDITS = 6;
export const partnerCockpitOfferRequestCreditsSchema = z
    .number()
    .int()
    .min(1)
    .max(PARTNER_COCKPIT_OFFER_REQUEST_MAX_CREDITS);
// Only a doubled Devis lead reaches 6, and the cockpit cannot yet tell a Devis partner from a Houzy
// one, so offering that filter to everyone would mostly show a chip that matches nothing. Extend this
// to the full range once the partner's team reaches the frontend.
export const PARTNER_COCKPIT_OFFER_REQUEST_FILTER_CREDIT_OPTIONS = [1, 2, 3, 4, 5];
// The first three are New Requests, the last three Open Requests.
export const partnerCockpitLeadStatusSchema = z.enum([
    'active',
    'declinedByPartner',
    'expired',
    'awaitingResponse',
    'rejectedByCustomer',
    'engaged',
]);
// `rejectedByCustomer` stays openable: the partner still has the conversation to read back.
export const OPENABLE_LEAD_STATUSES = new Set([
    'active',
    'awaitingResponse',
    'rejectedByCustomer',
    'engaged',
]);
export function isOpenableLeadStatus(leadStatus) {
    return OPENABLE_LEAD_STATUSES.has(leadStatus);
}
export const partnerCockpitOfferRequestSchema = z.object({
    uuid: z.uuid(),
    source: partnerCockpitOfferRequestSourceSchema,
    referenceId: z.string(),
    // Written by lead qualification; absent until then, when the client derives a title from the work categories.
    title: localizedTextSchema.nullable(),
    workCategoryKeys: z.array(z.string()),
    postalCode: z.string(),
    city: z.string(),
    // When the partner was matched to the request.
    matchedAt: z.iso.datetime(),
    // Absent when the match has no assignment, or the assignment carries no credit value (e.g. an
    // information-only request or a subscription partner)
    credits: partnerCockpitOfferRequestCreditsSchema.optional(),
    status: offerRequestReportStatusSchema,
    leadStatus: partnerCockpitLeadStatusSchema,
    // Only an active subscription-stream match can be declined; a bare assignment never can.
    canDecline: z.boolean().optional().default(false),
    isOnOldContract: z.boolean().optional().default(false),
    language: partnerCockpitOfferRequestLanguageSchema.optional(),
    // Absent for an information-only request.
    projectSize: offerRequestProjectSizeSchema.optional(),
    timeConstraint: offerRequestTimeConstraintSchema.optional(),
    buildingType: offerRequestBuildingTypeSchema.optional(),
    // Driving distance partner → request. Absent until partner-matching persists it on the assignment.
    distanceKm: z.number().optional(),
    // Absent when there is nothing to reimburse — no assignment behind the lead, or no credits deducted.
    reimbursement: partnerCockpitReimbursementStateSchema.optional(),
    // Credits actually given back, summed over the approved reimbursements. A partially approved
    // `TooManyCreditsBilled` request returns less than the lead cost, so this is not `credits`.
    reimbursedCredits: z.number().int().positive().optional(),
});
export const partnerCockpitOfferRequestsPageSchema = z.object({
    items: z.array(partnerCockpitOfferRequestSchema),
    total: z.number().int(),
});
export const partnerCockpitContactDetailSchema = z.enum(['phone', 'email']);
// Phone and email are withheld until the partner has sent its contact request (`awaitingResponse`), the address until
// the lead reaches `engaged`. The name is always present.
export const partnerCockpitOfferRequestContactSchema = z.object({
    name: z.string(),
    address: z.string().optional(),
    phone: z.string().optional(),
    email: z.email().optional(),
    revealedDetails: z.array(partnerCockpitContactDetailSchema),
});
export const partnerCockpitRevealContactDetailInputSchema = z.object({
    detail: partnerCockpitContactDetailSchema,
});
export const partnerCockpitOfferRequestDetailSchema = partnerCockpitOfferRequestSchema.extend({
    description: localizedTextSchema.nullable(),
    // The customer's offer-flow answers, minus the ones the dedicated fields above already show. Empty for the
    // legacy offer flows, whose answers the shareable report renders from hardcoded per-vertical layouts.
    moreInformation: z.array(offerRequestInfoFieldSchema),
    workCategoryPaths: z.array(z.array(z.string())),
    contact: partnerCockpitOfferRequestContactSchema,
    attachments: z.array(offerRequestAttachmentSchema),
    // True once an approved reimbursement took the lead back, which withholds phone and email. The
    // reimbursement reason stays server-side, so the carve-out is already resolved here.
    contactRestricted: z.boolean(),
});
export const partnerCockpitUpdateOfferRequestStatusSchema = z.object({
    status: settableOfferRequestReportStatusSchema,
});
// Whether this was the first time anyone at the partner opened the lead, on any surface.
export const recordOfferRequestOpeningResultSchema = z.object({
    isFirstOpening: z.boolean(),
});
